import styled from "styled-components";
import { pxToRem } from "../../styles/utils";

export const SelectRoot = styled.div<{ $fullWidth: boolean; $minWidth?: string }>`
  position: relative;
  display: inline-flex;
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "auto")};
  min-width: ${({ $minWidth }) => $minWidth || pxToRem(125)};
`;

export const SelectTrigger = styled.button<{ $open: boolean }>`
  width: 100%;
  height: ${pxToRem(40)};
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pxToRem(10)};
  padding: 0 ${pxToRem(32.5)} 0 ${pxToRem(12.5)};
  position: relative;
  border: ${pxToRem(1.25)} solid ${({ theme, $open }) =>
    $open ? theme.colors.brand : theme.colors.inkBorder};
  border-radius: ${pxToRem(10)};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.ink};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${pxToRem(16)};
  font-weight: 400;
  line-height: 1.25;
  white-space: nowrap;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.brand};
    background: ${({ theme }) => theme.colors.brandSubtle};
  }

  &:focus-visible {
    outline: ${pxToRem(2.5)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: -${pxToRem(1.25)};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

export const SelectValue = styled.span`
  display: inline-flex;
  flex: 1 1 auto;
  align-items: center;
  gap: ${pxToRem(10)};
  min-width: 0;
  max-width: 100%;
  overflow: visible;
  line-height: 1.25;

  > span:last-child {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.25;
  }
`;

export const SelectArrow = styled.span<{ $open: boolean }>`
  position: absolute;
  right: ${pxToRem(13.75)};
  top: 50%;
  width: ${pxToRem(7.5)};
  height: ${pxToRem(7.5)};
  border-right: ${pxToRem(1.25)} solid ${({ theme }) => theme.colors.inkMuted};
  border-bottom: ${pxToRem(1.25)} solid ${({ theme }) => theme.colors.inkMuted};
  transform: translateY(${({ $open }) => ($open ? "-25%" : "-70%")})
    rotate(${({ $open }) => ($open ? "225deg" : "45deg")});
  transition: transform 0.15s ease;
  pointer-events: none;
`;

export const SelectMenu = styled.div<{ $top: number; $left: number; $width: number }>`
  position: fixed;
  top: ${({ $top }) => `${$top}px`};
  left: ${({ $left }) => `${$left}px`};
  width: ${({ $width }) => `${$width}px`};
  min-width: ${pxToRem(125)};
  z-index: 10000;
  padding: ${pxToRem(5)};
  border: ${pxToRem(1.25)} solid ${({ theme }) => theme.colors.inkBorder};
  border-radius: ${pxToRem(10)};
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0 ${pxToRem(5)} ${pxToRem(15)} rgba(31, 10, 53, 0.12);
`;

export const SearchInput = styled.input`
  width: 100%;
  height: ${pxToRem(36)};
  margin-bottom: ${pxToRem(5)};
  padding: 0 ${pxToRem(10)};
  border: ${pxToRem(1.25)} solid ${({ theme }) => theme.colors.inkBorder};
  border-radius: ${pxToRem(8)};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.ink};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${pxToRem(16)};
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.brand};
  }
`;

export const OptionsList = styled.ul`
  max-height: ${pxToRem(275)};
  overflow-y: auto;
  margin: 0;
  padding: 0;
  list-style: none;
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.inkBorder} transparent;
`;

export const OptionButton = styled.button<{ $active: boolean; $selected: boolean }>`
  width: 100%;
  min-height: ${pxToRem(40)};
  display: flex;
  align-items: center;
  gap: ${pxToRem(10)};
  padding: ${pxToRem(7.5)} ${pxToRem(10)};
  border: none;
  border-radius: ${pxToRem(7.5)};
  background: ${({ theme, $active, $selected }) =>
    $active || $selected ? theme.colors.brandSubtle : "transparent"};
  color: ${({ theme, $selected }) => ($selected ? theme.colors.brand : theme.colors.ink)};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${pxToRem(16)};
  font-weight: ${({ $selected }) => ($selected ? 500 : 400)};
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.brandSubtle};
    color: ${({ theme }) => theme.colors.brand};
  }

  &:focus-visible {
    outline: none;
    background: ${({ theme }) => theme.colors.brandSubtle};
    color: ${({ theme }) => theme.colors.brand};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

export const EmptyOption = styled.div`
  padding: ${pxToRem(10)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${pxToRem(15)};
  text-align: center;
`;
